/**
 * Free-tier owner signup — `/owners/signup`, and `/r/:code` for a referral link.
 *
 * Owners reach the product by invitation today: an admin mints a token and
 * emails `/onboarding/:token`. This page lets an owner create the account
 * themselves and add a property afterwards.
 *
 * The account is provisioned by `handle_new_user()` on the `account_type`
 * metadata below, gated server-side by
 * `platform_settings.owner_self_signup_enabled` (20261012085001). The flag is
 * the authority; this page only decides what to show.
 *
 * The referral code is RECORDED, not redeemed. Owner-to-owner referrals are a
 * separate programme; the prototype already promises the free month only
 * "sobald CoLiviq Ihr Objekt freigegeben hat", so capturing the code at signup
 * is all this step owes anyone.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { isOwnerSignupOtpEnabled } from "@/lib/featureFlags";
import { classifySignInError } from "@/lib/owner-signin-error";
import { supabase } from "@/integrations/supabase/client";
import { Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** Referral codes look like PF-CLVQ-36: letters, digits and dashes. */
const REFERRAL_CODE = /^[A-Z0-9]{2,}-[A-Z0-9]{2,}-[A-Z0-9]{1,}$/;

const MIN_PASSWORD = 8;

export default function OwnerSignup() {
  const { t } = useLanguage();
  const { signUp, signIn } = useAuth();
  const navigate = useNavigate();
  const { code: codeFromPath } = useParams<{ code?: string }>();
  const [searchParams] = useSearchParams();

  // A referral link puts the code in the path (/r/PF-CLVQ-36); ?code= is kept
  // as a fallback so a pasted link with a query string still works.
  const referredCode = useMemo(() => {
    const raw = codeFromPath ?? searchParams.get("code") ?? "";
    return raw.trim().toUpperCase();
  }, [codeFromPath, searchParams]);

  // An owner who already has an account lands here too — from the Vermieter
  // CTA, or from a referral link a friend sent after they had signed up. The
  // signup tab is the default because that is what both entry points promise.
  const [mode, setMode] = useState<"signup" | "signin">("signup");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState(referredCode);
  // Set by "Keinen Code? Weiter ohne". Typing again clears it, so the note
  // never claims they continued without a code while one is in the field.
  const [codeSkipped, setCodeSkipped] = useState(false);
  // The inline "not recognised" note waits for blur, the way the prototype
  // does (touched.code || tried). Judging a half-typed code on every
  // keystroke paints the field red at the "P" of PF-CLVQ-36.
  const [codeTouched, setCodeTouched] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  /**
   * Has the owner pressed the button while the form was incomplete? Only then
   * is the red panel shown — nobody wants to be told what is missing from a
   * form they have not tried to submit yet.
   */
  const [showMissing, setShowMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  // null = still asking. The server gate decides whether an account created
  // here actually becomes an owner; with it off, submitting would make an
  // account that silently gets nothing and lands in the tenant dashboard.
  // Treat an unreachable RPC as off too — that means the migration is not
  // deployed, so provisioning genuinely cannot happen.
  const [signupOpen, setSignupOpen] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error } = await (
        supabase as unknown as {
          rpc: (fn: string) => Promise<{ data: unknown; error: unknown }>;
        }
      ).rpc("owner_self_signup_enabled");
      if (!cancelled) setSignupOpen(!error && data === true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const codeLooksWrong =
    code.trim().length > 0 && !REFERRAL_CODE.test(code.trim());

  /**
   * Is the signup form actually submittable?
   *
   * Neel, 2026-10-05: the button stayed enabled with the AGB box unticked, so
   * the only way to find out the box was required was to press the button and
   * be told off. The requirements are all visible next to the button — the
   * password rule has its own hint line, the checkbox sits directly above —
   * so refusing the press is clearer than accepting it and failing.
   *
   * The referral code is deliberately NOT part of this: it is optional, and
   * "Keinen Code? Weiter ohne" exists precisely so a missing one never blocks.
   *
   * The button is NOT `disabled` — it stays pressable so a press can answer
   * why. That means implicit submission reaches `handleSubmit` too: Enter in
   * the Name field submits the form and shows the panel, which is the same
   * answer a click gives. The handler re-derives the list rather than
   * trusting this flag.
   */
  const passwordsMatch =
    confirmPassword.length > 0 && password === confirmPassword;

  /**
   * Every unmet requirement, in the order the fields appear.
   *
   * Neel, 2026-10-07: a disabled button that explains nothing is its own dead
   * end — you can see it is refusing you but not why. So the button stays
   * visually disabled and keeps `aria-disabled`, but remains pressable, and a
   * press with anything missing lists ALL of it at once. Listing one failure
   * at a time would walk them through the form error by error.
   */
  const missingRequirements = useMemo<string[]>(() => {
    const missing: string[] = [];
    if (!name.trim()) missing.push(t("ownerSignup.missingName", "Name fehlt."));
    if (!email.trim())
      missing.push(t("ownerSignup.missingEmail", "E-Mail-Adresse fehlt."));
    if (password.length < MIN_PASSWORD)
      missing.push(
        // Built from MIN_PASSWORD rather than spelled out: a hardcoded "8"
        // keeps claiming 8 after the constant moves, while the check below
        // enforces the new length.
        t(
          "ownerSignup.missingPassword",
          "Passwort: mindestens {n} Zeichen."
        ).replace("{n}", String(MIN_PASSWORD))
      );
    else if (!passwordsMatch)
      missing.push(
        t("ownerSignup.missingConfirm", "Passwörter stimmen nicht überein.")
      );
    if (!acceptedTerms)
      missing.push(
        t("ownerSignup.missingTerms", "AGB und Datenschutz nicht bestätigt.")
      );
    return missing;
  }, [name, email, password, passwordsMatch, acceptedTerms, t]);

  /**
   * The button's own state, derived from the SAME list the panel prints, so
   * the two can never disagree about whether the form is ready.
   */
  const canSubmitSignup = missingRequirements.length === 0;
  /** Shown only once they have left the field — see codeTouched. */
  const showCodeError = codeLooksWrong && codeTouched;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // The gate may still be in flight. Submitting now would create an account
    // the trigger refuses to make an owner, while this page reports a
    // successful owner registration.
    if (signupOpen !== true) return;

    // The button is pressable while incomplete purely so this can answer it.
    if (!canSubmitSignup) {
      setShowMissing(true);
      return;
    }
    setShowMissing(false);

    // Not re-checked field by field here: `canSubmitSignup` is derived from
    // the same values in the same render, so any branch that could fail has
    // already returned above. The code check below is NOT in that list --
    // it is deliberately optional, so it is still the handler's to make.
    // A malformed code is worth stopping on rather than silently dropping:
    // the owner believes it earns them a free month.
    if (codeLooksWrong) {
      setError(
        t("ownerSignup.errorCode", "Code nicht erkannt. Format: PF-CLVQ-36.")
      );
      return;
    }

    setSubmitting(true);
    try {
      const { error: signUpError } = await signUp(
        email.trim(),
        password,
        name.trim(),
        {
          redirectPath: "/owners/confirmed",
          metadata: {
            account_type: "owner",
            // Recorded for the referral programme; nothing is credited here.
            referral_code: code.trim() || null,
          },
        }
      );
      if (signUpError) {
        setError(
          signUpError.message ??
            t("ownerSignup.errorGeneric", "Etwas ist schiefgelaufen.")
        );
        return;
      }
      // With the OTP flag on the owner types a code instead of hunting for
      // the link; with it off they keep the existing "check your email" card.
      if (isOwnerSignupOtpEnabled()) {
        navigate(`/owners/verify?email=${encodeURIComponent(email.trim())}`);
        return;
      }
      setSentTo(email.trim());
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !password) {
      setError(
        t("ownerSignup.signInMissing", "Bitte E-Mail und Passwort eingeben.")
      );
      return;
    }

    setSubmitting(true);
    try {
      const { error: signInError } = await signIn(email.trim(), password);
      if (signInError) {
        // Named in German (Neel, 2026-10-05). Supabase's own English message
        // still arrives as a toast from AuthContext; this is the line the
        // owner actually reads, next to the field they would otherwise retype.
        const failure = classifySignInError(signInError);
        setError(
          failure === "unconfirmed"
            ? t(
                "ownerSignup.signInUnconfirmed",
                "Ihre E-Mail-Adresse ist noch nicht bestätigt. Bitte öffnen Sie den Bestätigungslink aus unserer E-Mail — sehen Sie auch im Spam-Ordner nach."
              )
            : failure === "rate_limited"
              ? t(
                  "ownerSignup.signInRateLimited",
                  "Zu viele Versuche. Bitte warten Sie ein paar Minuten und versuchen Sie es erneut."
                )
              : // Wrong password AND unknown address land here together, on
                // purpose: see classifySignInError.
                t(
                  "ownerSignup.signInFailed",
                  "E-Mail oder Passwort stimmt nicht."
                )
        );
        return;
      }
      // No navigate() here on purpose. `AuthContext.signIn` already routes on
      // success, and it is the only place that can route CORRECTLY: it knows
      // the roles it just loaded, and it consumes the deep link
      // `ProtectedRoute` stashed (`takeReturnPath`) before bouncing the visitor
      // out. A second navigate here ran after that one and overrode it — so an
      // owner who arrived from a protected link lost it (the stash is consumed
      // either way, so it was destroyed rather than deferred), and anyone
      // signing in on this page who is NOT owner-or-staff was sent to an
      // /admin route `ProtectedRoute` immediately bounces again.
    } finally {
      setSubmitting(false);
    }
  }

  if (signupOpen === false) {
    return (
      <Shell>
        <div className="rounded-2xl border border-[#ECE7DD] bg-white p-6 text-center shadow-[0_12px_32px_rgba(15,76,74,0.06)]">
          <h1 className="text-xl font-bold tracking-tight text-[#0F4C4A]">
            {t("ownerSignup.closedTitle", "Noch nicht verfügbar")}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-[#47605E]">
            {t(
              "ownerSignup.closedBody",
              "Die Selbstregistrierung für Eigentümer ist noch nicht freigeschaltet. Schreiben Sie uns an info@coliviq.de — wir legen Ihr Konto an."
            )}
          </p>
        </div>
      </Shell>
    );
  }

  if (sentTo) {
    return (
      <Shell>
        <div className="text-center">
          <span className="w-14 h-14 rounded-full bg-[#E3EFED] text-[#0F4C4A] inline-flex items-center justify-center mb-4">
            <Mail className="w-6 h-6" />
          </span>
          <h1 className="text-xl font-bold text-[#0F4C4A] tracking-tight">
            {t("ownerSignup.checkTitle", "E-Mail bestätigen")}
          </h1>
          <p className="mt-2 text-sm text-[#47605E] leading-relaxed">
            {t(
              "ownerSignup.checkBody",
              "Wir haben Ihnen einen Bestätigungslink geschickt an"
            )}{" "}
            <span className="font-semibold text-[#1A2B2A]">{sentTo}</span>.
          </p>
          <p className="mt-4 text-xs text-[#7A8886]">
            {t(
              "ownerSignup.checkHint",
              "Keine E-Mail erhalten? Sehen Sie im Spam-Ordner nach."
            )}
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      {referredCode && (
        <div className="mb-4 rounded-2xl border border-[#ECE7DD] bg-white p-4">
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="text-[12.5px] font-semibold text-[#0F4C4A]">
              {t("ownerSignup.referredBy", "Empfehlung")}
            </span>
            <span className="px-2.5 py-1 rounded-full bg-[#E3EFED] text-[#0F4C4A] text-xs font-bold tracking-wider tabular-nums">
              {referredCode}
            </span>
          </div>
          <p className="mt-2 text-[12.5px] leading-relaxed text-[#47605E]">
            {t(
              "ownerSignup.referredBody",
              "Ein Monat Verwaltung gratis — für Sie beide, sobald coliviq Ihr Objekt freigegeben hat."
            )}
          </p>
        </div>
      )}

      <div className="rounded-2xl border border-[#ECE7DD] bg-white p-6 shadow-[0_12px_32px_rgba(15,76,74,0.06)]">
        {/* The prototype uses the tab pair as the heading — a separate <h1>
            above it would say "Konto erstellen" while the Anmelden tab is
            selected. */}
        <div
          role="tablist"
          className="flex rounded-full border border-[#ECE7DD] bg-[#FAF8F4] p-1"
        >
          {(["signup", "signin"] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={mode === m}
              onClick={() => {
                setMode(m);
                // An error from the other tab ("Bitte AGB akzeptieren") makes
                // no sense once the fields it referred to are gone.
                setError(null);
                // Same for the missing-requirements panel. The fields are
                // shared state, so without this a press on "Konto erstellen",
                // a switch to "Anmelden" and a switch back brought the red
                // role="alert" panel straight back -- announced again by a
                // screen reader, with no new press. It appears after a press
                // or not at all.
                setShowMissing(false);
              }}
              className={`flex-1 h-10 rounded-full text-sm font-semibold transition-colors ${
                mode === m
                  ? "bg-[#0F4C4A] text-white"
                  : "text-[#47605E] hover:text-[#0F4C4A]"
              }`}
            >
              {m === "signup"
                ? t("ownerSignup.title", "Konto erstellen")
                : t("ownerSignup.signInTab", "Anmelden")}
            </button>
          ))}
        </div>

        {mode === "signin" ? (
          <form onSubmit={handleSignIn} noValidate>
            <div className="mt-5 space-y-4">
              <div>
                <Label
                  htmlFor="owner-signin-email"
                  className="text-xs text-[#47605E]"
                >
                  {t("ownerSignup.email", "E-Mail")}
                </Label>
                <Input
                  id="owner-signin-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t(
                    "ownerSignup.emailPlaceholder",
                    "name@beispiel.de"
                  )}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label
                  htmlFor="owner-signin-password"
                  className="text-xs text-[#47605E]"
                >
                  {t("ownerSignup.password", "Passwort")}
                </Label>
                <Input
                  id="owner-signin-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5"
                />
              </div>
            </div>

            {error && (
              <p
                role="alert"
                className="mt-4 text-sm text-[#C0714F] leading-relaxed"
              >
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="mt-5 w-full h-12 rounded-xl bg-[#0F4C4A] hover:bg-[#0B3A38] text-white text-base font-semibold disabled:opacity-60"
            >
              {submitting
                ? t("ownerSignup.signingIn", "Wird angemeldet …")
                : t("ownerSignup.signInTab", "Anmelden")}
            </Button>

            <p className="mt-4 text-center">
              <Link
                to="/forgot-password"
                className="text-sm font-semibold text-[#2FB3AA] hover:underline"
              >
                {t("ownerSignup.forgotPassword", "Passwort vergessen?")}
              </Link>
            </p>
          </form>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <p className="mt-4 text-sm text-[#7A8886]">
              {t(
                "ownerSignup.subtitle",
                "Kostenlos. Ihre Immobilie fügen Sie später im Portal hinzu."
              )}
            </p>

            <div className="mt-5 space-y-4">
              <div>
                <Label htmlFor="owner-name" className="text-xs text-[#47605E]">
                  {t("ownerSignup.name", "Name")}
                </Label>
                <Input
                  id="owner-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t(
                    "ownerSignup.namePlaceholder",
                    "Vor- und Nachname"
                  )}
                  autoComplete="name"
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="owner-email" className="text-xs text-[#47605E]">
                  {t("ownerSignup.email", "E-Mail")}
                </Label>
                <Input
                  id="owner-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@beispiel.de"
                  autoComplete="email"
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label
                  htmlFor="owner-password"
                  className="text-xs text-[#47605E]"
                >
                  {t("ownerSignup.password", "Passwort")}
                </Label>
                <Input
                  id="owner-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  className="mt-1.5"
                />
                <p className="mt-1.5 text-xs text-[#7A8886]">
                  {t("ownerSignup.passwordHint", "Mindestens 8 Zeichen.")}
                </p>
              </div>

              <div>
                <Label
                  htmlFor="owner-password-confirm"
                  className="text-xs text-[#47605E]"
                >
                  {t("ownerSignup.passwordConfirm", "Passwort bestätigen")}
                </Label>
                <Input
                  id="owner-password-confirm"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  aria-invalid={confirmPassword.length > 0 && !passwordsMatch}
                  className="mt-1.5"
                />
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="mt-1.5 text-xs text-[#C0714F]">
                    {t(
                      "ownerSignup.confirmMismatch",
                      "Passwörter stimmen nicht überein."
                    )}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="owner-code" className="text-xs text-[#47605E]">
                  {t("ownerSignup.code", "Empfehlungscode")}
                </Label>
                <Input
                  id="owner-code"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value.toUpperCase());
                    setCodeSkipped(false);
                  }}
                  onBlur={() => setCodeTouched(true)}
                  placeholder="PF-CLVQ-36"
                  className="mt-1.5 tracking-wider tabular-nums"
                />
                <div className="mt-1.5 flex items-start justify-between gap-3">
                  <p
                    className={`text-xs ${
                      showCodeError ? "text-[#C0714F]" : "text-[#7A8886]"
                    }`}
                  >
                    {showCodeError
                      ? t(
                          "ownerSignup.codeNotRecognised",
                          "Code nicht erkannt. Format: PF-CLVQ-36."
                        )
                      : codeSkipped
                        ? t(
                            "ownerSignup.codeSkipped",
                            "Ohne Code fortgefahren."
                          )
                        : referredCode
                          ? t(
                              "ownerSignup.codeFromLink",
                              "Aus Ihrem Empfehlungslink."
                            )
                          : t("ownerSignup.codeOptional", "Optional.")}
                  </p>
                  {/* A malformed code BLOCKS the form — it is worth stopping on,
                  because the owner believes it earns them a free month. But
                  then they need a way out that is not "select the field and
                  delete it": one tap clears it and says so. */}
                  {code.trim().length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setCode("");
                        setCodeSkipped(true);
                        setCodeTouched(false);
                        setError(null);
                      }}
                      className="shrink-0 whitespace-nowrap text-xs font-medium text-[#2FB3AA] hover:text-[#0F4C4A]"
                    >
                      {t("ownerSignup.noCode", "Keinen Code? Weiter ohne")}
                    </button>
                  )}
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer text-sm text-[#1A2B2A] leading-relaxed">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 text-[#0F4C4A] focus:ring-[#2FB3AA] shrink-0"
                />
                <span>
                  {t("ownerSignup.termsPre", "Ich akzeptiere die ")}
                  <Link
                    to="/terms-of-service"
                    className="text-[#2FB3AA] underline underline-offset-2"
                  >
                    {t("ownerSignup.termsAgb", "AGB")}
                  </Link>
                  {t("ownerSignup.termsMid", " und die ")}
                  <Link
                    to="/privacy-policy"
                    className="text-[#2FB3AA] underline underline-offset-2"
                  >
                    {t("ownerSignup.termsPrivacy", "Datenschutzerklärung")}
                  </Link>
                  .
                </span>
              </label>
            </div>

            {error && (
              <p
                role="alert"
                className="mt-4 text-sm text-[#C0714F] leading-relaxed"
              >
                {error}
              </p>
            )}

            {/* Recomputed every render rather than frozen at press time: an
                owner who ticks the AGB box should watch that line disappear,
                not be told about it again after a second press. */}
            {showMissing && missingRequirements.length > 0 && (
              <div
                role="alert"
                className="mt-4 rounded-xl border border-[#E3B7A3] bg-[#FDF3EE] px-4 py-3"
              >
                <p className="text-sm font-semibold text-[#A8542F]">
                  {t(
                    "ownerSignup.missingTitle",
                    "Bitte vervollständigen Sie noch:"
                  )}
                </p>
                <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-sm leading-relaxed text-[#C0714F]">
                  {missingRequirements.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              </div>
            )}

            <Button
              type="submit"
              /* Not `disabled` when merely incomplete: a disabled button
                 swallows the click, and then nothing can explain what is
                 missing. It still LOOKS disabled and reports itself as such to
                 assistive tech; handleSubmit answers the press. */
              disabled={submitting || signupOpen !== true}
              aria-disabled={!canSubmitSignup}
              /* The `disabled:` variants stopped applying when the native
                 attribute was dropped, so the dimmed button still darkened on
                 hover and showed a pointer -- it looked disabled and felt
                 enabled. Hover and cursor are turned off explicitly while
                 anything is missing; it stays pressable, which is the point. */
              className={`mt-5 w-full h-12 rounded-xl bg-[#0F4C4A] text-white text-base font-semibold disabled:opacity-60 ${
                canSubmitSignup
                  ? "hover:bg-[#0B3A38]"
                  : "opacity-60 hover:bg-[#0F4C4A] cursor-default"
              }`}
            >
              {submitting
                ? t("ownerSignup.submitting", "Wird erstellt …")
                : t("ownerSignup.submit", "Konto erstellen")}
            </Button>

            <p className="mt-4 text-center text-xs text-[#7A8886]">
              {t(
                "ownerSignup.contractNote",
                "Das Konto ist kostenlos. Der Verwaltungsvertrag folgt, sobald coliviq Ihr Objekt freigegeben hat."
              )}
            </p>
          </form>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    // No header of its own: the public site nav already renders above this
    // route, and a second wordmark reads as a broken page. That nav is `fixed`
    // at 72px though, so the page owes it that much clearance or the card
    // slides underneath it — and the centring is against the space that is
    // LEFT once it is accounted for, or a centred card sits visibly low.
    <div className="bg-[#FAF8F4] pt-[72px]">
      <main className="flex min-h-[calc(100vh-72px)] flex-col items-center justify-center px-4 py-7 sm:px-6 sm:py-[52px]">
        <div className="w-full max-w-[400px]">{children}</div>
      </main>
    </div>
  );
}
